// src/components/research/math.js

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

/* Light maths typesetting, without a formula engine.
   ------------------------------------------------------------------------
   Every equation on this page is one line of standard notation, so KaTeX or
   MathJax would be a dependency, a stylesheet and a webfont in exchange for
   nothing these cannot do: a subscript, a superscript, and correct upright /
   italic discipline.

   The convention is the usual one and is enforced by the CSS rather than by
   the call site: everything is upright by default, and only what is wrapped in
   <i> is a variable. That is why operators, digits, parentheses and function
   names come out right without anyone having to remember to mark them.

   A serif face, deliberately. The page is set in Calibre, and an italic
   Calibre "f" beside a Calibre sentence reads as emphasis rather than as a
   symbol. The switch of family is what tells a reader this is notation. */

const MATH_FAMILY = `'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, 'Times New Roman', serif`;

const mathType = `
  font-family: ${MATH_FAMILY};
  font-style: normal;
  font-variant-numeric: lining-nums;

  i {
    font-style: italic;
    /* Serif italics run narrow and collide with what follows them, most
       visibly in f(x) and in a subscripted symbol. */
    padding-right: 0.04em;
  }

  sub,
  sup {
    font-size: 0.72em;
    font-style: normal;
    line-height: 0;
  }

  /* Binary relations and operators want air on both sides; the browser gives
     a space the same width as a word space, which is too little at this size. */
  .op {
    padding: 0 0.32em;
  }

  /* A wider break between two statements that share a line. */
  .sep {
    display: inline-block;
    width: 1.6em;
  }

  /* One statement. Wrapped this way, the only place a line can break is
     between two statements, so a narrow screen stacks them instead of
     splitting a formula across the middle of itself. */
  .stmt {
    display: inline-block;
    white-space: nowrap;
  }
`;

const StyledEq = styled.div`
  max-width: var(--measure);
  margin: var(--space-6) 0;
  text-align: center;

  .line {
    display: block;
    color: var(--text-emphasis);
    font-size: calc(var(--fz-lg) + 1px);
    /* Not --lh-body. Subscripts and superscripts sit outside the line box, so
       an equation set at prose leading has its indices touching the line above
       it. This is the one place on the site that legitimately runs looser. */
    line-height: 1.6;
    ${mathType};

    /* Statements do not wrap internally, so on the narrowest screens the
       margin has to come from the type instead. */
    @media (max-width: 480px) {
      font-size: var(--fz-md);
    }
  }

  .line + .line {
    margin-top: var(--space-3);
  }

  /* The equation's own caption: what the symbols are, and what the line is
     doing in the argument. Deliberately not another paragraph — it is attached
     to the equation and travels with it. */
  .caption {
    max-width: 34rem;
    margin: var(--space-3) auto 0;
    color: var(--text-muted);
    font-family: var(--font-sans);
    font-size: var(--fz-sm);
    line-height: var(--lh-body);
    text-align: center;
  }
`;

const StyledInline = styled.span`
  color: var(--text-emphasis);
  font-size: 1.02em;
  white-space: nowrap;
  ${mathType};
`;

export const EqLine = ({ children }) => <span className="line">{children}</span>;

EqLine.propTypes = { children: PropTypes.node.isRequired };

export const Eq = ({ note, children }) => (
  <StyledEq>
    {children}
    {note ? <p className="caption">{note}</p> : null}
  </StyledEq>
);

Eq.propTypes = {
  note: PropTypes.node,
  children: PropTypes.node.isRequired,
};

/* Inline, for a symbol named mid sentence. nowrap so a formula never breaks
   across a line, which is the one thing that makes light typesetting look
   like a mistake rather than a choice. */
export const MathText = ({ children }) => <StyledInline>{children}</StyledInline>;

MathText.propTypes = { children: PropTypes.node.isRequired };
